/**
 * 用氣習慣問卷（冷啟動 onboarding）。
 *
 * 正式寫入：POST /app-api/customer/gas-survey（見 gasSurveyApi.ts）。
 * 是否顯示：以 GET /customer/home 的 `survey_prompt` 為準。
 * localStorage 僅在 API **成功後**作 cache（禁止樂觀更新）。
 */

/** 距上次叫桶（粗分桶；日後可進模型錨點） */
export type LastOrderAgoKey =
  | "within_7"
  | "within_14"
  | "within_30"
  | "within_90"
  | "longer"
  | "unknown";

/** 在家節奏（日後可對齊暫停通知／behavior） */
export type OccupancyKey = "mostly_home" | "often_away" | "sometimes_empty";

export interface GasSurveyRecord {
  /** 自陳「預計多久叫一次桶」（天） */
  survey_days: number;
  /** 家中鋼瓶規格（kg）；「不確定」存 null */
  cylinder_kg: number | null;
  /** 選填：最近兩次叫桶相隔天數 */
  observed_interval_days: number | null;
  /** 完成時間 ISO string */
  completed_at: string;
  /** 家庭人數："1" | "2" | "3-4" | "5+" */
  household_size?: string | null;
  /** 主要用氣用途（多選）："cooking" | "hot_water" | "tea" */
  usage_types?: string[] | null;
  /** 選填：距上次叫桶多久（粗分） */
  last_order_ago?: LastOrderAgoKey | null;
  /** 選填：對應粗分的代表天數（用於錨點推導） */
  last_order_ago_days?: number | null;
  /** 選填：在家節奏（舊版欄位，新問卷不再收集） */
  occupancy?: OccupancyKey | null;
  /** 選填：是否有備瓶 */
  has_spare_cylinder?: boolean | null;
}

export const LAST_ORDER_AGO_CHIPS: { key: LastOrderAgoKey; label: string; days: number | null }[] = [
  { key: "within_7", label: "一週內", days: 4 },
  { key: "within_14", label: "約 2 週前", days: 14 },
  { key: "within_30", label: "約 1 個月前", days: 30 },
  { key: "within_90", label: "約 2–3 個月前", days: 75 },
  { key: "longer", label: "更久以前", days: 120 },
  { key: "unknown", label: "不記得", days: null },
];

/** 自訂「距上次叫桶」天數上限（對齊 API @Max(730)） */
export const LAST_ORDER_AGO_DAYS_MAX = 730;

/** 自訂天數 → 後端 last_order_ago enum 分桶 */
export const mapDaysToLastOrderAgoKey = (days: number): LastOrderAgoKey => {
  if (days <= 7) return "within_7";
  if (days <= 14) return "within_14";
  if (days <= 30) return "within_30";
  if (days <= 90) return "within_90";
  return "longer";
};

export const OCCUPANCY_CHIPS: { key: OccupancyKey; label: string }[] = [
  { key: "mostly_home", label: "幾乎每天在家" },
  { key: "often_away", label: "常出差／不在" },
  { key: "sometimes_empty", label: "有時空著" },
];

const storageKey = (contextId: number | string | null | undefined): string =>
  `gas_survey_v1:${contextId ?? "default"}`;

export const getGasSurvey = (contextId: number | string | null | undefined): GasSurveyRecord | null => {
  try {
    const raw = localStorage.getItem(storageKey(contextId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GasSurveyRecord;
    if (!Number.isFinite(parsed?.survey_days)) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const saveGasSurvey = (contextId: number | string | null | undefined, record: GasSurveyRecord): void => {
  try {
    localStorage.setItem(storageKey(contextId), JSON.stringify(record));
  } catch {
    /* Safari 私密模式等寫入失敗時靜默；下次進來會再看到問卷 CTA */
  }
};

/**
 * DEV／測試用：一組可直接 POST 的完整問卷答案（不寫 localStorage）。
 * 方便跳過逐步點選，直接打 gas-survey → predict_one。
 */
export const buildDevDefaultSurveyRecord = (
  overrides?: Partial<GasSurveyRecord>,
): GasSurveyRecord => ({
  survey_days: 30,
  cylinder_kg: 20,
  observed_interval_days: null,
  completed_at: new Date().toISOString(),
  household_size: "2",
  usage_types: ["cooking", "hot_water"],
  last_order_ago: "within_30",
  last_order_ago_days: 30,
  has_spare_cylinder: false,
  ...overrides,
});

/** 問卷建議更新週期（天）；後端就緒後改由 API 回傳 */
export const SURVEY_REFRESH_DAYS = 90;

export type SurveyNeedReason = "never" | "refresh";

export interface SurveyNeed {
  needed: boolean;
  reason: SurveyNeedReason | null;
}

/** 判斷是否需顯示問卷：未填過，或距上次填寫已超過更新週期 */
export const needsGasSurvey = (record: GasSurveyRecord | null | undefined): SurveyNeed => {
  if (!record) return { needed: true, reason: "never" };
  const completed = new Date(record.completed_at);
  if (Number.isNaN(completed.getTime())) return { needed: true, reason: "never" };
  const daysSince = Math.floor((Date.now() - completed.getTime()) / 86_400_000);
  if (daysSince >= SURVEY_REFRESH_DAYS) return { needed: true, reason: "refresh" };
  return { needed: false, reason: null };
};

/**
 * 以問卷（＋選填實測週期）粗估叫桶週期。
 * 對齊冷啟動 demo 的權重：實測算 1 個觀測、問卷自陳算 0.5 個觀測。
 * 純前端展示用，非正式模型輸出。
 */
export const estimateCycleFromSurvey = (record: GasSurveyRecord): { predictedDays: number; dispatchLeadDays: number } => {
  const observed = record.observed_interval_days;
  const predictedDays =
    observed && observed > 0
      ? Math.round((observed * 1 + record.survey_days * 0.5) / 1.5)
      : Math.round(record.survey_days);
  return {
    predictedDays,
    dispatchLeadDays: Math.max(2, Math.round(predictedDays * 0.1)),
  };
};

/** YYYY-MM-DD；優先訂單日 → 問卷推估上次叫桶 → 問卷完成日 */
export const resolveSurveyAnchorDate = (
  record: GasSurveyRecord,
  lastOrderDate?: string | null,
): string => {
  if (lastOrderDate && /^\d{4}-\d{2}-\d{2}/.test(lastOrderDate)) {
    return lastOrderDate.slice(0, 10);
  }
  const ago = record.last_order_ago_days;
  if (ago != null && ago >= 0) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - Math.round(ago));
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return record.completed_at.slice(0, 10);
};
